import logger from '../utils/logger';
/**
 * PERSISTENT CACHE SYSTEM - IndexedDB
 * 
 * Système de cache ultra-performant pour données utilisateur
 * - Cache persistent (survit au refresh)
 * - Affichage instantané (< 10ms)
 * - Sync background avec Supabase Realtime
 * 
 * Architecture identique à Notion, Linear, ChatGPT
 */


// ============================================
// CONFIGURATION
// ============================================

const DB_NAME = 'clairefy_cache';
const DB_VERSION = 3; // v3: Fix missing user_id index on insights store
const CACHE_VERSION_KEY = 'clairefy_cache_version';
const CURRENT_CACHE_VERSION = '3.0.0'; // Increment pour forcer clear
const STORES = {
  // Core
  profiles: 'profiles',
  sessions: 'chat_sessions',
  messages: 'chat_messages',
  
  // Analytics & Insights
  insights: 'insights',
  chat_insights: 'chat_insights',
  deep_reports: 'deep_reports',
  session_analyses: 'session_analyses',
  
  // Business Intelligence
  business_patterns: 'business_patterns',
  pattern_collisions: 'pattern_collisions',
  business_maturity_scores: 'business_maturity_scores',
  
  // Dashboards & Viz
  dashboards: 'dashboards',
  dashboard_widgets: 'dashboard_widgets',
  
  // Data Management
  raw_data: 'raw_data',
  user_files: 'user_files',
  data_sources: 'data_sources',
  data_imports: 'data_imports',
  chat_data_imports: 'chat_data_imports',
  
  // Billing & Tokens
  subscriptions: 'subscriptions',
  token_transactions: 'token_transactions',
  token_plans: 'token_plans',
  stripe_payments: 'stripe_payments',
  
  // User Management
  api_keys: 'api_keys',
  user_integrations: 'user_integrations',
  referrals: 'referrals',
  
  // Logs & Admin (cache limité, pour debug seulement)
  audit_logs: 'audit_logs',
  system_logs: 'system_logs',
} as const;

// ============================================
// INDEXEDDB SETUP
// ============================================

let dbInstance: IDBDatabase | null = null;

/**
 * Initialise la base de données IndexedDB
 */
async function initDB(): Promise<IDBDatabase> {
 if (dbInstance) return dbInstance;

 return new Promise((resolve, reject) => {
 const request = indexedDB.open(DB_NAME, DB_VERSION);

 request.onerror = () => {
 logger.error(' [IndexedDB] Error opening database');
 reject(request.error);
 };

 request.onsuccess = () => {
 dbInstance = request.result;
 logger.log(' [IndexedDB] Database opened successfully');
 resolve(dbInstance);
 };

 request.onupgradeneeded = (event) => {
 const db = (event.target as IDBOpenDBRequest).result;
 
 logger.log(' [IndexedDB] Upgrading database schema...');

 // Store profiles
 if (!db.objectStoreNames.contains(STORES.profiles)) {
 const profileStore = db.createObjectStore(STORES.profiles, { keyPath: 'id' });
 profileStore.createIndex('updated_at', 'updated_at', { unique: false });
 logger.log(' [IndexedDB] Created profiles store');
 }

 // Store chat sessions
 if (!db.objectStoreNames.contains(STORES.sessions)) {
 const sessionStore = db.createObjectStore(STORES.sessions, { keyPath: 'id' });
 sessionStore.createIndex('user_id', 'user_id', { unique: false });
 sessionStore.createIndex('created_at', 'created_at', { unique: false });
 logger.log(' [IndexedDB] Created sessions store');
 }

 // Store messages
 if (!db.objectStoreNames.contains(STORES.messages)) {
 const messageStore = db.createObjectStore(STORES.messages, { keyPath: 'id' });
 messageStore.createIndex('session_id', 'session_id', { unique: false });
 messageStore.createIndex('created_at', 'created_at', { unique: false });
 logger.log(' [IndexedDB] Created messages store');
 }

    // Store insights
    if (!db.objectStoreNames.contains(STORES.insights)) {
      const insightStore = db.createObjectStore(STORES.insights, { keyPath: 'id' });
      insightStore.createIndex('session_id', 'session_id', { unique: false });
      insightStore.createIndex('user_id', 'user_id', { unique: false });
      logger.log(' [IndexedDB] Created insights store');
    } else {
      // Migration v2 -> v3: Add user_id index if missing
      const tx = (event.target as IDBOpenDBRequest).transaction;
      if (tx) {
        const insightStore = tx.objectStore(STORES.insights);
        if (!insightStore.indexNames.contains('user_id')) {
          insightStore.createIndex('user_id', 'user_id', { unique: false });
          logger.log(' [IndexedDB] Added user_id index to insights store (migration)');
        }
      }
    }

    // Store chat_insights
    if (!db.objectStoreNames.contains(STORES.chat_insights)) {
      const chatInsightStore = db.createObjectStore(STORES.chat_insights, { keyPath: 'id' });
      chatInsightStore.createIndex('session_id', 'session_id', { unique: false });
      chatInsightStore.createIndex('user_id', 'user_id', { unique: false });
      logger.log(' [IndexedDB] Created chat_insights store');
    }

    // Store deep_reports
    if (!db.objectStoreNames.contains(STORES.deep_reports)) {
      const deepReportStore = db.createObjectStore(STORES.deep_reports, { keyPath: 'id' });
      deepReportStore.createIndex('user_id', 'user_id', { unique: false });
      deepReportStore.createIndex('created_at', 'created_at', { unique: false });
      logger.log(' [IndexedDB] Created deep_reports store');
    }

    // Store session_analyses
    if (!db.objectStoreNames.contains(STORES.session_analyses)) {
      const sessionAnalysisStore = db.createObjectStore(STORES.session_analyses, { keyPath: 'id' });
      sessionAnalysisStore.createIndex('session_id', 'session_id', { unique: false });
      sessionAnalysisStore.createIndex('user_id', 'user_id', { unique: false });
      logger.log(' [IndexedDB] Created session_analyses store');
    }

    // Store business_patterns
    if (!db.objectStoreNames.contains(STORES.business_patterns)) {
      const patternStore = db.createObjectStore(STORES.business_patterns, { keyPath: 'id' });
      patternStore.createIndex('user_id', 'user_id', { unique: false });
      patternStore.createIndex('session_id', 'session_id', { unique: false });
      patternStore.createIndex('created_at', 'created_at', { unique: false });
      logger.log(' [IndexedDB] Created business_patterns store');
    }

    // Store pattern_collisions
    if (!db.objectStoreNames.contains(STORES.pattern_collisions)) {
      const collisionStore = db.createObjectStore(STORES.pattern_collisions, { keyPath: 'id' });
      collisionStore.createIndex('user_id', 'user_id', { unique: false });
      collisionStore.createIndex('pattern_a_id', 'pattern_a_id', { unique: false });
      collisionStore.createIndex('pattern_b_id', 'pattern_b_id', { unique: false });
      logger.log(' [IndexedDB] Created pattern_collisions store');
    }

    // Store business_maturity_scores
    if (!db.objectStoreNames.contains(STORES.business_maturity_scores)) {
      const maturityStore = db.createObjectStore(STORES.business_maturity_scores, { keyPath: 'id' });
      maturityStore.createIndex('user_id', 'user_id', { unique: false });
      maturityStore.createIndex('created_at', 'created_at', { unique: false });
      logger.log(' [IndexedDB] Created business_maturity_scores store');
    }

    // Store dashboards
    if (!db.objectStoreNames.contains(STORES.dashboards)) {
      const dashboardStore = db.createObjectStore(STORES.dashboards, { keyPath: 'id' });
      dashboardStore.createIndex('user_id', 'user_id', { unique: false });
      dashboardStore.createIndex('created_at', 'created_at', { unique: false });
      logger.log(' [IndexedDB] Created dashboards store');
    }

    // Store dashboard_widgets
    if (!db.objectStoreNames.contains(STORES.dashboard_widgets)) {
      const widgetStore = db.createObjectStore(STORES.dashboard_widgets, { keyPath: 'id' });
      widgetStore.createIndex('dashboard_id', 'dashboard_id', { unique: false });
      widgetStore.createIndex('user_id', 'user_id', { unique: false });
      logger.log(' [IndexedDB] Created dashboard_widgets store');
    }

    // Store raw_data
    if (!db.objectStoreNames.contains(STORES.raw_data)) {
      const rawDataStore = db.createObjectStore(STORES.raw_data, { keyPath: 'id' });
      rawDataStore.createIndex('session_id', 'session_id', { unique: false });
      rawDataStore.createIndex('user_id', 'user_id', { unique: false });
      logger.log(' [IndexedDB] Created raw_data store');
    }

    // Store user_files
    if (!db.objectStoreNames.contains(STORES.user_files)) {
      const fileStore = db.createObjectStore(STORES.user_files, { keyPath: 'id' });
      fileStore.createIndex('user_id', 'user_id', { unique: false });
      fileStore.createIndex('created_at', 'created_at', { unique: false });
      logger.log(' [IndexedDB] Created user_files store');
    }

    // Store data_sources
    if (!db.objectStoreNames.contains(STORES.data_sources)) {
      const sourceStore = db.createObjectStore(STORES.data_sources, { keyPath: 'id' });
      sourceStore.createIndex('user_id', 'user_id', { unique: false });
      logger.log(' [IndexedDB] Created data_sources store');
    }

    // Store data_imports
    if (!db.objectStoreNames.contains(STORES.data_imports)) {
      const importStore = db.createObjectStore(STORES.data_imports, { keyPath: 'id' });
      importStore.createIndex('user_id', 'user_id', { unique: false });
      importStore.createIndex('created_at', 'created_at', { unique: false });
      logger.log(' [IndexedDB] Created data_imports store');
    }

    // Store chat_data_imports
    if (!db.objectStoreNames.contains(STORES.chat_data_imports)) {
      const chatImportStore = db.createObjectStore(STORES.chat_data_imports, { keyPath: 'id' });
      chatImportStore.createIndex('user_id', 'user_id', { unique: false });
      chatImportStore.createIndex('created_at', 'created_at', { unique: false });
      logger.log(' [IndexedDB] Created chat_data_imports store');
    }

    // Store subscriptions
    if (!db.objectStoreNames.contains(STORES.subscriptions)) {
      const subscriptionStore = db.createObjectStore(STORES.subscriptions, { keyPath: 'id' });
      subscriptionStore.createIndex('user_id', 'user_id', { unique: false });
      subscriptionStore.createIndex('status', 'status', { unique: false });
      logger.log(' [IndexedDB] Created subscriptions store');
    }

    // Store token_transactions
    if (!db.objectStoreNames.contains(STORES.token_transactions)) {
      const tokenTxStore = db.createObjectStore(STORES.token_transactions, { keyPath: 'id' });
      tokenTxStore.createIndex('user_id', 'user_id', { unique: false });
      tokenTxStore.createIndex('created_at', 'created_at', { unique: false });
      logger.log(' [IndexedDB] Created token_transactions store');
    }

    // Store token_plans
    if (!db.objectStoreNames.contains(STORES.token_plans)) {
      const planStore = db.createObjectStore(STORES.token_plans, { keyPath: 'id' });
      planStore.createIndex('active', 'active', { unique: false });
      logger.log(' [IndexedDB] Created token_plans store');
    }

    // Store stripe_payments
    if (!db.objectStoreNames.contains(STORES.stripe_payments)) {
      const paymentStore = db.createObjectStore(STORES.stripe_payments, { keyPath: 'id' });
      paymentStore.createIndex('user_id', 'user_id', { unique: false });
      paymentStore.createIndex('created_at', 'created_at', { unique: false });
      logger.log(' [IndexedDB] Created stripe_payments store');
    }

    // Store api_keys
    if (!db.objectStoreNames.contains(STORES.api_keys)) {
      const apiKeyStore = db.createObjectStore(STORES.api_keys, { keyPath: 'id' });
      apiKeyStore.createIndex('user_id', 'user_id', { unique: false });
      apiKeyStore.createIndex('active', 'active', { unique: false });
      logger.log(' [IndexedDB] Created api_keys store');
    }

    // Store user_integrations
    if (!db.objectStoreNames.contains(STORES.user_integrations)) {
      const integrationStore = db.createObjectStore(STORES.user_integrations, { keyPath: 'id' });
      integrationStore.createIndex('user_id', 'user_id', { unique: false });
      integrationStore.createIndex('platform', 'platform', { unique: false });
      logger.log(' [IndexedDB] Created user_integrations store');
    }

    // Store referrals
    if (!db.objectStoreNames.contains(STORES.referrals)) {
      const referralStore = db.createObjectStore(STORES.referrals, { keyPath: 'id' });
      referralStore.createIndex('referrer_id', 'referrer_id', { unique: false });
      referralStore.createIndex('referred_id', 'referred_id', { unique: false });
      logger.log(' [IndexedDB] Created referrals store');
    }

    // Store audit_logs (limité aux 100 derniers pour debug)
    if (!db.objectStoreNames.contains(STORES.audit_logs)) {
      const auditStore = db.createObjectStore(STORES.audit_logs, { keyPath: 'id' });
      auditStore.createIndex('user_id', 'user_id', { unique: false });
      auditStore.createIndex('created_at', 'created_at', { unique: false });
      logger.log(' [IndexedDB] Created audit_logs store');
    }

    // Store system_logs (limité aux 100 derniers pour debug)
    if (!db.objectStoreNames.contains(STORES.system_logs)) {
      const sysLogStore = db.createObjectStore(STORES.system_logs, { keyPath: 'id' });
      sysLogStore.createIndex('user_id', 'user_id', { unique: false });
      sysLogStore.createIndex('created_at', 'created_at', { unique: false });
      logger.log(' [IndexedDB] Created system_logs store');
    }
  };
});
}

// ============================================
// GENERIC OPERATIONS
// ============================================

/**
 * Sauvegarde un objet dans IndexedDB
 */
export async function cacheSet<T>(
 storeName: keyof typeof STORES,
 key: string,
 value: T
): Promise<void> {
 try {
 const db = await initDB();
 const tx = db.transaction(STORES[storeName], 'readwrite');
 const store = tx.objectStore(STORES[storeName]);

 // Ajouter timestamp pour tracking
 const dataWithMeta = {
 ...(value as any),
 id: key,
 _cached_at: Date.now(),
 };

 const request = store.put(dataWithMeta);

 return new Promise((resolve, reject) => {
 request.onsuccess = () => {
 logger.log(` [IndexedDB] Cached ${storeName}:${key}`);
 resolve();
 };
 request.onerror = () => {
 logger.error(` [IndexedDB] Error caching ${storeName}:${key}`);
 reject(request.error);
 };
 });
 } catch (error) {
 logger.error(` [IndexedDB] Error in cacheSet:`, error);
 throw error;
 }
}

/**
 * Récupère un objet depuis IndexedDB
 */
export async function cacheGet<T>(
 storeName: keyof typeof STORES,
 key: string
): Promise<T | null> {
 try {
 const db = await initDB();
 const tx = db.transaction(STORES[storeName], 'readonly');
 const store = tx.objectStore(STORES[storeName]);
 const request = store.get(key);

 return new Promise((resolve, reject) => {
 request.onsuccess = () => {
 if (request.result) {
 const age = Date.now() - (request.result._cached_at || 0);
 logger.log(` [IndexedDB] Cache HIT ${storeName}:${key} (age: ${age}ms)`);
 resolve(request.result as T);
 } else {
 logger.log(`[IndexedDB] Cache MISS ${storeName}:${key}`);
 resolve(null);
 }
 };
 request.onerror = () => {
 logger.error(` [IndexedDB] Error getting ${storeName}:${key}`);
 reject(request.error);
 };
 });
 } catch (error) {
 logger.error(` [IndexedDB] Error in cacheGet:`, error);
 return null;
 }
}

/**
 * Récupère tous les objets d'un store
 */
export async function cacheGetAll<T>(
 storeName: keyof typeof STORES,
 indexName?: string,
 indexValue?: any
): Promise<T[]> {
 try {
 const db = await initDB();
 const tx = db.transaction(STORES[storeName], 'readonly');
 const store = tx.objectStore(STORES[storeName]);

 let request: IDBRequest;
 if (indexName && indexValue !== undefined) {
 const index = store.index(indexName);
 request = index.getAll(indexValue);
 } else {
 request = store.getAll();
 }

 return new Promise((resolve, reject) => {
 request.onsuccess = () => {
 logger.log(` [IndexedDB] Got ${request.result.length} items from ${storeName}`);
 resolve(request.result as T[]);
 };
 request.onerror = () => {
 logger.error(` [IndexedDB] Error getting all from ${storeName}`);
 reject(request.error);
 };
 });
 } catch (error) {
 logger.error(` [IndexedDB] Error in cacheGetAll:`, error);
 return [];
 }
}

/**
 * Supprime un objet d'IndexedDB
 */
export async function cacheDelete(
 storeName: keyof typeof STORES,
 key: string
): Promise<void> {
 try {
 const db = await initDB();
 const tx = db.transaction(STORES[storeName], 'readwrite');
 const store = tx.objectStore(STORES[storeName]);
 const request = store.delete(key);

 return new Promise((resolve, reject) => {
 request.onsuccess = () => {
 logger.log(` [IndexedDB] Deleted ${storeName}:${key}`);
 resolve();
 };
 request.onerror = () => {
 logger.error(` [IndexedDB] Error deleting ${storeName}:${key}`);
 reject(request.error);
 };
 });
 } catch (error) {
 logger.error(` [IndexedDB] Error in cacheDelete:`, error);
 throw error;
 }
}

/**
 * Vide tout un store
 */
export async function cacheClear(storeName: keyof typeof STORES): Promise<void> {
 try {
 const db = await initDB();
 const tx = db.transaction(STORES[storeName], 'readwrite');
 const store = tx.objectStore(STORES[storeName]);
 const request = store.clear();

 return new Promise((resolve, reject) => {
 request.onsuccess = () => {
 logger.log(` [IndexedDB] Cleared ${storeName}`);
 resolve();
 };
 request.onerror = () => {
 logger.error(` [IndexedDB] Error clearing ${storeName}`);
 reject(request.error);
 };
 });
 } catch (error) {
 logger.error(` [IndexedDB] Error in cacheClear:`, error);
 throw error;
 }
}

// ============================================
// HIGH-LEVEL API - ULTRA COMPLETE
// ============================================

export const PersistentCache = {
  // ========== CORE ==========
  
  // Profile
  saveProfile: (profile: any) => cacheSet('profiles', profile.id, profile),
  getProfile: (userId: string) => cacheGet('profiles', userId),
  
  // Chat Sessions
  saveSessions: async (sessions: any[]) => {
    for (const session of sessions) {
      await cacheSet('sessions', session.id, session);
    }
  },
  getSessions: (userId: string) => cacheGetAll('sessions', 'user_id', userId),
  deleteSession: (sessionId: string) => cacheDelete('sessions', sessionId),
  
  // Messages
  saveMessages: async (sessionId: string, messages: any[]) => {
    for (const msg of messages) {
      await cacheSet('messages', msg.id, { ...msg, session_id: sessionId });
    }
  },
  getMessages: (sessionId: string) => cacheGetAll('messages', 'session_id', sessionId),
  
  // ========== ANALYTICS & INSIGHTS ==========
  
  // Insights (general)
  saveInsights: async (insights: any[]) => {
    for (const insight of insights) {
      await cacheSet('insights', insight.id, insight);
    }
  },
  getInsightsByUser: (userId: string) => cacheGetAll('insights', 'user_id', userId),
  
  // Chat Insights
  saveChatInsights: async (sessionId: string, insights: any[]) => {
    for (const insight of insights) {
      await cacheSet('chat_insights', insight.id, { ...insight, session_id: sessionId });
    }
  },
  getChatInsights: (sessionId: string) => cacheGetAll('chat_insights', 'session_id', sessionId),
  
  // Deep Reports
  saveDeepReports: async (reports: any[]) => {
    for (const report of reports) {
      await cacheSet('deep_reports', report.id, report);
    }
  },
  getDeepReports: (userId: string) => cacheGetAll('deep_reports', 'user_id', userId),
  
  // Session Analyses
  saveSessionAnalyses: async (analyses: any[]) => {
    for (const analysis of analyses) {
      await cacheSet('session_analyses', analysis.id, analysis);
    }
  },
  getSessionAnalyses: (userId: string) => cacheGetAll('session_analyses', 'user_id', userId),
  getSessionAnalysis: (sessionId: string) => cacheGetAll('session_analyses', 'session_id', sessionId),
  
  // ========== BUSINESS INTELLIGENCE ==========
  
  // Business Patterns
  saveBusinessPatterns: async (patterns: any[]) => {
    for (const pattern of patterns) {
      await cacheSet('business_patterns', pattern.id, pattern);
    }
  },
  getBusinessPatterns: (userId: string) => cacheGetAll('business_patterns', 'user_id', userId),
  
  // Pattern Collisions
  savePatternCollisions: async (collisions: any[]) => {
    for (const collision of collisions) {
      await cacheSet('pattern_collisions', collision.id, collision);
    }
  },
  getPatternCollisions: (userId: string) => cacheGetAll('pattern_collisions', 'user_id', userId),
  
  // Business Maturity Scores
  saveBusinessMaturityScores: async (scores: any[]) => {
    for (const score of scores) {
      await cacheSet('business_maturity_scores', score.id, score);
    }
  },
  getBusinessMaturityScores: (userId: string) => cacheGetAll('business_maturity_scores', 'user_id', userId),
  
  // ========== DASHBOARDS & VIZ ==========
  
  // Dashboards
  saveDashboards: async (dashboards: any[]) => {
    for (const dashboard of dashboards) {
      await cacheSet('dashboards', dashboard.id, dashboard);
    }
  },
  getDashboards: (userId: string) => cacheGetAll('dashboards', 'user_id', userId),
  
  // Dashboard Widgets
  saveDashboardWidgets: async (widgets: any[]) => {
    for (const widget of widgets) {
      await cacheSet('dashboard_widgets', widget.id, widget);
    }
  },
  getDashboardWidgets: (dashboardId: string) => cacheGetAll('dashboard_widgets', 'dashboard_id', dashboardId),
  
  // ========== DATA MANAGEMENT ==========
  
  // Raw Data
  saveRawData: async (data: any[]) => {
    for (const item of data) {
      await cacheSet('raw_data', item.id, item);
    }
  },
  getRawData: (sessionId: string) => cacheGetAll('raw_data', 'session_id', sessionId),
  
  // User Files
  saveUserFiles: async (files: any[]) => {
    for (const file of files) {
      await cacheSet('user_files', file.id, file);
    }
  },
  getUserFiles: (userId: string) => cacheGetAll('user_files', 'user_id', userId),
  
  // Data Sources
  saveDataSources: async (sources: any[]) => {
    for (const source of sources) {
      await cacheSet('data_sources', source.id, source);
    }
  },
  getDataSources: (userId: string) => cacheGetAll('data_sources', 'user_id', userId),
  
  // Data Imports
  saveDataImports: async (imports: any[]) => {
    for (const imp of imports) {
      await cacheSet('data_imports', imp.id, imp);
    }
  },
  getDataImports: (userId: string) => cacheGetAll('data_imports', 'user_id', userId),
  
  // Chat Data Imports
  saveChatDataImports: async (imports: any[]) => {
    for (const imp of imports) {
      await cacheSet('chat_data_imports', imp.id, imp);
    }
  },
  getChatDataImports: (userId: string) => cacheGetAll('chat_data_imports', 'user_id', userId),
  
  // ========== BILLING & TOKENS ==========
  
  // Subscriptions
  saveSubscriptions: async (subscriptions: any[]) => {
    for (const sub of subscriptions) {
      await cacheSet('subscriptions', sub.id, sub);
    }
  },
  getSubscriptions: (userId: string) => cacheGetAll('subscriptions', 'user_id', userId),
  
  // Token Transactions
  saveTokenTransactions: async (transactions: any[]) => {
    for (const tx of transactions) {
      await cacheSet('token_transactions', tx.id, tx);
    }
  },
  getTokenTransactions: (userId: string) => cacheGetAll('token_transactions', 'user_id', userId),
  
  // Token Plans
  saveTokenPlans: async (plans: any[]) => {
    for (const plan of plans) {
      await cacheSet('token_plans', plan.id, plan);
    }
  },
  getTokenPlans: () => cacheGetAll('token_plans', 'active', true),
  
  // Stripe Payments
  saveStripePayments: async (payments: any[]) => {
    for (const payment of payments) {
      await cacheSet('stripe_payments', payment.id, payment);
    }
  },
  getStripePayments: (userId: string) => cacheGetAll('stripe_payments', 'user_id', userId),
  
  // ========== USER MANAGEMENT ==========
  
  // API Keys
  saveApiKeys: async (keys: any[]) => {
    for (const key of keys) {
      await cacheSet('api_keys', key.id, key);
    }
  },
  getApiKeys: (userId: string) => cacheGetAll('api_keys', 'user_id', userId),
  
  // User Integrations
  saveUserIntegrations: async (integrations: any[]) => {
    for (const integration of integrations) {
      await cacheSet('user_integrations', integration.id, integration);
    }
  },
  getUserIntegrations: (userId: string) => cacheGetAll('user_integrations', 'user_id', userId),
  
  // Referrals
  saveReferrals: async (referrals: any[]) => {
    for (const referral of referrals) {
      await cacheSet('referrals', referral.id, referral);
    }
  },
  getReferralsByReferrer: (referrerId: string) => cacheGetAll('referrals', 'referrer_id', referrerId),
  getReferralsByReferred: (referredId: string) => cacheGetAll('referrals', 'referred_id', referredId),
  
  // ========== LOGS & ADMIN ==========
  
  // Audit Logs (limités aux 100 derniers)
  saveAuditLogs: async (logs: any[]) => {
    const limited = logs.slice(-100); // Garder seulement les 100 derniers
    for (const log of limited) {
      await cacheSet('audit_logs', log.id, log);
    }
  },
  getAuditLogs: (userId: string) => cacheGetAll('audit_logs', 'user_id', userId),
  
  // System Logs (limités aux 100 derniers)
  saveSystemLogs: async (logs: any[]) => {
    const limited = logs.slice(-100); // Garder seulement les 100 derniers
    for (const log of limited) {
      await cacheSet('system_logs', log.id, log);
    }
  },
  getSystemLogs: (userId: string) => cacheGetAll('system_logs', 'user_id', userId),
  
  // ========== CLEANUP ==========
  clearAll: async () => {
    const allStores = Object.keys(STORES) as Array<keyof typeof STORES>;
    for (const store of allStores) {
      await cacheClear(store);
    }
    logger.log(' [IndexedDB] ALL stores cleared');
  },
};

// ============================================
// CACHE VERSION MANAGEMENT (AUTO-CLEAR)
// ============================================

/**
 * Vérifie la version du cache et clear automatiquement si obsolète
 * CRITIQUE: Empêche les users d'être bloqués avec un cache corrompu
 */
async function checkCacheVersion(): Promise<void> {
  try {
    const storedVersion = localStorage.getItem(CACHE_VERSION_KEY);
    
    if (storedVersion !== CURRENT_CACHE_VERSION) {
      logger.warn(` [Cache] Version mismatch: ${storedVersion} -> ${CURRENT_CACHE_VERSION}`);
      logger.warn(' [Cache] AUTO-CLEAR pour éviter cache corrompu...');
      
      // Fermer la connexion actuelle
      if (dbInstance) {
        dbInstance.close();
        dbInstance = null;
      }
      
      // Supprimer TOUTE la base
      await new Promise<void>((resolve, reject) => {
        const deleteRequest = indexedDB.deleteDatabase(DB_NAME);
        deleteRequest.onsuccess = () => {
          logger.log(' [Cache] Base supprimée avec succès');
          resolve();
        };
        deleteRequest.onerror = () => {
          logger.error(' [Cache] Erreur suppression base');
          reject(deleteRequest.error);
        };
      });
      
      // Mettre à jour la version stockée
      localStorage.setItem(CACHE_VERSION_KEY, CURRENT_CACHE_VERSION);
      logger.log(` [Cache] Version mise à jour: ${CURRENT_CACHE_VERSION}`);
      
      // Réinitialiser
      await initDB();
    } else {
      logger.log(` [Cache] Version OK: ${CURRENT_CACHE_VERSION}`);
    }
  } catch (error) {
    logger.error(' [Cache] Erreur vérification version:', error);
    // En cas d'erreur, forcer le clear
    try {
      localStorage.setItem(CACHE_VERSION_KEY, CURRENT_CACHE_VERSION);
      if (dbInstance) {
        dbInstance.close();
        dbInstance = null;
      }
      await new Promise<void>((resolve) => {
        const deleteRequest = indexedDB.deleteDatabase(DB_NAME);
        deleteRequest.onsuccess = () => resolve();
        deleteRequest.onerror = () => resolve(); // Continuer même si échec
      });
      await initDB();
    } catch (e) {
      logger.error(' [Cache] Impossible de réparer le cache:', e);
    }
  }
}

// ============================================
// INITIALIZATION
// ============================================

// Init IndexedDB au chargement du module avec vérification de version
(async () => {
  try {
    await checkCacheVersion(); // Vérifier AVANT initDB
    await initDB();
  } catch (error) {
    logger.error(' [IndexedDB] Failed to initialize:', error);
  }
})();

// ============================================
// EMERGENCY CACHE CLEAR (for corrupted DB)
// ============================================

/**
 * Force clear corrupted IndexedDB (exposed globally for debug)
 */
export async function emergencyClearCache(): Promise<void> {
  try {
    logger.warn(' [IndexedDB] EMERGENCY: Clearing corrupted cache...');
    
    // Close current connection
    if (dbInstance) {
      dbInstance.close();
      dbInstance = null;
    }
    
    // Delete entire database
    await new Promise<void>((resolve, reject) => {
      const deleteRequest = indexedDB.deleteDatabase(DB_NAME);
      deleteRequest.onsuccess = () => {
        logger.log(' [IndexedDB] Database deleted successfully');
        resolve();
      };
      deleteRequest.onerror = () => {
        logger.error(' [IndexedDB] Error deleting database');
        reject(deleteRequest.error);
      };
    });
    
    // Reinitialize
    await initDB();
    logger.log(' [IndexedDB] Cache cleared and reinitialized');
    
    // Reload page to reset everything
    window.location.reload();
  } catch (error) {
    logger.error(' [IndexedDB] Emergency clear failed:', error);
  }
}

// Expose globally for emergency use
if (typeof window !== 'undefined') {
  (window as any).clairefyDebug = {
    ...(window as any).clairefyDebug,
    emergencyClearCache,
  };
}

